import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';

import { type DateRange } from 'react-day-picker';

import { type SourcingMetricsFilterRange } from 'api-clients/monolith';

import { HiringGoalModal } from '@/components/pages/channels/hiring-goals-modal';
import {
  LIFETIME,
  type DateRangePreset,
} from '@/components/ui/date-range-picker';
import { HireStats } from '@/components/ui/hire-stats';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useMetricsFilterParams } from '@/hooks/use-metrics-filter-params';
import { useOpeningSlug } from '@/hooks/use-opening-slug';

import { DateRangePanel } from './date-range-panel';
import { DateRangeView } from './date-range-view';
import {
  applyMetricsFilterToUrl,
  getBackendMetricsDateRangeState,
  getMetricsFilterApply,
  resolveMetricsDisplayState,
} from './date-range-view/filter-utils';
import { HiringGoalView } from './hiring-goal-view';
import { type MetricsViewMode, MetricsViewToggle } from './metrics-view-toggle';

function Divider() {
  return <div className="bg-fx-neutral-200 w-px self-stretch" aria-hidden />;
}

function HiringGoalContent() {
  const openingSlug = useOpeningSlug();
  const {
    opening,
    hiringGoal,
    hiringGoalEditUrl,
    filterDuration,
    hasMultipleActiveGoals,
  } = useOpeningDetails();
  const {
    startDate,
    endDate,
    filterRange,
    metricsViewMode,
    setMetricsViewMode,
  } = useOpeningContext();
  const { applyFilterRange, setMetricsFilter } = useMetricsFilterParams();
  // Applying a date range or switching tabs changes `useOpeningDetails()`'s
  // query key (it includes startDate/endDate/filterRange), which would
  // otherwise re-suspend this whole panel — unmounting the toggle and
  // picker the user just clicked. Wrapping those updates in a transition
  // keeps the current content on screen (with `isPending` to show it's
  // stale) instead of falling back to `HiringGoal.Skeleton`.
  const [isPending, startTransition] = useTransition();

  // The transitioned state above only reflects the new tab/range once the
  // underlying fetch resolves, which would otherwise make the toggle and
  // date picker look unresponsive to the click that triggered them. These
  // track "what the user just chose" so the controls update immediately;
  // once the transition settles they're cleared and the real (by-then
  // matching) values take back over.
  const [optimisticViewMode, setOptimisticViewMode] =
    useState<MetricsViewMode | null>(null);
  const [optimisticRange, setOptimisticRange] = useState<{
    preset: DateRangePreset;
    range: DateRange;
  } | null>(null);

  useEffect(() => {
    if (!isPending) {
      setOptimisticViewMode(null);
      setOptimisticRange(null);
    }
  }, [isPending]);

  const displayedViewMode = optimisticViewMode ?? metricsViewMode;

  const dateRangeTabDefaults = useMemo(
    () => getBackendMetricsDateRangeState(opening.defaultMetricsPeriod, null),
    [opening.defaultMetricsPeriod],
  );

  const [isHiringGoalModalOpen, setIsHiringGoalModalOpen] = useState(false);

  const hasUrlMetricsFilter =
    Boolean(startDate && endDate) || Boolean(filterRange);

  const metricsDisplayState = useMemo(
    () =>
      resolveMetricsDisplayState({
        startDate,
        endDate,
        filterRange,
        defaultMetricsPeriod: opening.defaultMetricsPeriod,
        filterDuration,
      }),
    [
      startDate,
      endDate,
      filterRange,
      opening.defaultMetricsPeriod,
      filterDuration,
    ],
  );

  const handleViewModeChange = useCallback(
    (mode: MetricsViewMode) => {
      setOptimisticViewMode(mode);
      startTransition(() => {
        if (mode === 'date_range') {
          const switchingFromHiringGoal = metricsViewMode === 'hiring_goal';
          if (switchingFromHiringGoal) {
            const { preset, urlApply } = dateRangeTabDefaults;
            const defaultPeriod: SourcingMetricsFilterRange | null =
              preset === LIFETIME
                ? LIFETIME
                : urlApply?.type === 'filter_range'
                  ? urlApply.filterRange
                  : null;
            setMetricsViewMode('date_range', defaultPeriod);
          } else {
            // Already in date_range — preserve the user's manual URL filter.
            setMetricsViewMode('date_range');
          }
        } else {
          setMetricsViewMode('hiring_goal');
        }
      });
    },
    [metricsViewMode, dateRangeTabDefaults, setMetricsViewMode],
  );

  useLayoutEffect(() => {
    if (hasUrlMetricsFilter) {
      setMetricsViewMode('date_range');
    }
  }, [hasUrlMetricsFilter, setMetricsViewMode]);

  const handleMetricsRangeApply = useCallback(
    ({ preset, range }: { preset?: DateRangePreset; range: DateRange }) => {
      const apply = getMetricsFilterApply(preset, range);
      if (!apply) return;

      setOptimisticRange({ preset: preset ?? 'custom', range });
      startTransition(() => {
        applyMetricsFilterToUrl(apply, { applyFilterRange, setMetricsFilter });
      });
    },
    [applyFilterRange, setMetricsFilter],
  );

  return (
    <div className="flex w-[570px] shrink-0 flex-col gap-4 pb-4">
      <MetricsViewToggle
        value={displayedViewMode}
        onChange={handleViewModeChange}
        isPending={isPending}
      />

      {metricsViewMode === 'date_range' ? (
        <DateRangeView
          appliedDate={optimisticRange?.range ?? metricsDisplayState.range}
          appliedPreset={optimisticRange?.preset ?? metricsDisplayState.preset}
          onApply={handleMetricsRangeApply}
          openingSlug={openingSlug}
          isPending={isPending}
        />
      ) : (
        <HiringGoalView
          hiringGoal={hiringGoal}
          hiringGoalEditUrl={hiringGoalEditUrl}
          onViewPastGoals={() => setIsHiringGoalModalOpen(true)}
          isPending={isPending}
          hasMultipleActiveGoals={hasMultipleActiveGoals}
        />
      )}

      <HiringGoalModal
        open={isHiringGoalModalOpen}
        onOpenChange={setIsHiringGoalModalOpen}
      />
    </div>
  );
}

/**
 * Wrapped in its own Suspense boundary so its `useOpeningDetails()` call
 * doesn't block (or get blocked by) `OverviewHeader`, its sibling in
 * `Overview` — each fires its own fetches independently and in parallel.
 */
export function HiringGoal() {
  return (
    <Suspense fallback={<HiringGoal.Skeleton />}>
      <HiringGoalContent />
    </Suspense>
  );
}

function HiringGoalContentSkeleton() {
  return (
    <div className="flex min-h-[80px] items-start gap-6">
      <DateRangePanel.Skeleton />
      <Divider />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Skeleton className="h-[12px] w-[32px]" />
        <HireStats.Skeleton />
      </div>
    </div>
  );
}

HiringGoal.Skeleton = function HiringGoalSkeleton() {
  const { metricsViewMode } = useOpeningContext();
  return (
    <div className="flex w-[570px] shrink-0 flex-col gap-4 pb-4">
      <MetricsViewToggle
        value={metricsViewMode}
        onChange={() => {}}
        disabled={true}
      />
      <HiringGoalContentSkeleton />
    </div>
  );
};
